'use client';

import type { FC } from 'react';
import { Credits } from './Credits';

interface AmountSentenceProps {
  /** Localized sentence carrying exactly one `{{amount}}` slot. */
  template: string;
  amount: number;
  className?: string;
  iconSize?: number;
}

/** A sentence with a credit amount inside it — the coin glyph belongs to `Credits`, so the string
 *  arrives with a `{{amount}}` slot and is cut at it instead of being interpolated. The slot is not
 *  always at the end: Uzbek puts the verb AFTER the sum («… — 1 000 yoʻqotding», «… yana 500
 *  toʻldir»), which a plain «text + number» concatenation cannot express — that concatenation is
 *  exactly what produced the broken word order in #323.
 *
 *  A locale that drops the placeholder fails quietly: `split` returns one part, `after` becomes
 *  `undefined`, and the sentence just ends early. Every dictionary using this component is covered
 *  by a test asserting the slot survives — see `Roulette.translations.test.ts` and
 *  `AchievementClaimBanner.translations.test.ts`. */
export const AmountSentence: FC<AmountSentenceProps> = ({
  template,
  amount,
  className,
  iconSize = 13,
}) => {
  const [before, after] = template.split('{{amount}}');
  return (
    <span className={className}>
      {before}
      <Credits amount={amount} iconSize={iconSize} align="baseline" />
      {after}
    </span>
  );
};
